import { useCallback, useRef } from 'react';
import { ApiTimeout, useBackendApi } from '@/hooks/fetch';
import { nativeActionAttemptStore } from '@/lib/nativeActionIdempotency';
import type { NativeActionIdempotencyRequest } from '@shared/nativeActionApiTypes';
import { useAuth } from '@/hooks/auth';

type NativeActionHookOptions = {
    path: string;
    keyPrefix: string;
};

const ambiguousActionErrors = new Set([
    'bridge_send_failed',
    'bridge_timeout',
    'bridge_shutdown',
    'request_timeout',
    'idempotency_in_progress',
    'idempotency_uncertain',
    'internal_error',
    'audit_outcome_uncertain',
    'legacy_intent_uncertain',
    'legacy_projection_failed',
]);

export const useNativeActionApi = <
    RespType,
    ReqType extends Record<string, unknown>,
>({ path, keyPrefix }: NativeActionHookOptions) => {
    const { authData } = useAuth();
    const actorId = authData ? authData.name : 'unauthenticated';
    const actionApi = useBackendApi<RespType, ReqType & NativeActionIdempotencyRequest>({
        method: 'POST',
        path,
    });
    const actionApiRef = useRef(actionApi);
    actionApiRef.current = actionApi;

    type ActionApiOptions = Parameters<typeof actionApi>[0];
    type NativeActionApiOptions = Omit<ActionApiOptions, 'data'> & { data: ReqType };

    return useCallback(async (options: NativeActionApiOptions) => {
        const attempt = await nativeActionAttemptStore.begin(actorId, keyPrefix, {
            pathParams: options.pathParams,
            queryParams: options.queryParams,
            data: options.data,
        });
        const originalError = options.error;
        const response = await actionApiRef.current({
            ...options,
            timeout: options.timeout ?? ApiTimeout.REALLY_REALLY_LONG,
            data: { ...options.data, idempotencyKey: attempt.key },
            error: (message, toastId) => originalError?.(message, toastId),
        });
        const code = response && typeof response === 'object' && 'code' in response
            ? (response as { code?: unknown }).code
            : undefined;
        if (response !== undefined && (typeof code !== 'string' || !ambiguousActionErrors.has(code))) {
            nativeActionAttemptStore.complete(attempt);
        }
        return response;
    }, [actorId, keyPrefix]);
};
